import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "@/store";
import { useNotification } from "@/context/NotificationContext";
import * as reactionsApi from "@/api/reactions.api";
import {
  ReactionKind,
  reactionKindToSummaryKey,
} from "@/constants/reaction-kind";
import { hr } from "@/constants/strings.hr";
import type { ReactionSummary } from "@/types";

export function useArticleReactions(articleId: string) {
  const { isAuthenticated } = useAppSelector((s) => s.auth);
  const { notify } = useNotification();
  const [summary, setSummary] = useState<ReactionSummary>({
    thumbsUp: 0,
    thumbsDown: 0,
  });
  const [userReaction, setUserReaction] = useState<ReactionKind | null>(null);

  useEffect(() => {
    reactionsApi.getReactionSummary(articleId).then(setSummary);
  }, [articleId]);

  const toggleReaction = useCallback(
    async (type: ReactionKind) => {
      if (!isAuthenticated) {
        notify(hr.reactions.loginRequired, "info");
        return;
      }

      try {
        if (userReaction === type) {
          await reactionsApi.removeReaction(articleId);
          setUserReaction(null);
          const key = reactionKindToSummaryKey(type);
          setSummary((prev) => ({
            ...prev,
            [key]: Math.max(0, prev[key] - 1),
          }));
        } else {
          await reactionsApi.upsertReaction(articleId, type);
          setSummary((prev) => {
            const next = { ...prev };
            if (userReaction) {
              const oldKey = reactionKindToSummaryKey(userReaction);
              next[oldKey] = Math.max(0, next[oldKey] - 1);
            }
            const newKey = reactionKindToSummaryKey(type);
            next[newKey] = next[newKey] + 1;
            return next;
          });
          setUserReaction(type);
        }
      } catch {
        notify(hr.reactions.submitError, "error");
      }
    },
    [articleId, isAuthenticated, notify, userReaction],
  );

  return { summary, userReaction, toggleReaction };
}
